import { StandardProps } from "@/types/types";
import styles from "./Facets.module.scss";
import classNames from "classnames/bind";
import { FacetGroup } from "./components/FacetGroup/FacetGroup";
import { FacetItem } from "./components/FacetItem/FacetItem";
import { useEffect, useState, useRef } from "react";
import { TertiaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { MinusOutlined as Minus24PxOutlinedIcon } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/MinusOutlined";
import { PlusOutlined as Plus24PxOutlinedIcon } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/PlusOutlined";
import { dispatchAnalyticsEvent } from "@/utils/analytics";
import { useFeatureFlags } from "@/flags/flags";
import { getFacetsWithSelectedValues } from "@/utils/setJLData";
import { FILTER_INTERACTION } from "@/utils/events";
import { useRouteParams } from "@/hooks/useRouteParams";
import { sendMonetateEvent } from "@/utils/sendMonetateEvent";
import { createDispatchFacetAnalytics } from "./utils/createDispatchFacetAnalytics";

const cx = classNames.bind(styles);

const SINGLE_ITEM_DIMENSIONS = [
  "childSKUs.hideOutOfStock",
  "product.newIn",
  "exclusiveToJl",
];

const DEPARTMENT_DIMENSION = "Store.Department";

const INITIAL_NUMBER_TO_SHOW = 5;
const MAX_NUMBER_TO_SHOW_BEFORE_PROMPTING = 7;
const NUMBER_OF_OPEN_FACET_ACCORDIONS = 3;

export function Facets({
  productListingData,
  configProps,
  targetFacetName,
}: Pick<StandardProps, "productListingData" | "configProps"> & {
  targetFacetName?: string;
}) {
  const route = useRouteParams();
  const { isFacetLimitOn, isAllFacetsClosedFlagOn } = useFeatureFlags();

  const [isFacetListExpanded, setIsFacetListExpanded] = useState(false);
  const firstUpdate = useRef(true);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
    } else {
      dispatchAnalyticsEvent(FILTER_INTERACTION, {
        type: isFacetListExpanded ? "show more" : "show fewer",
      });
    }
  }, [isFacetListExpanded]);

  const { facets } = productListingData;

  const singleItemFacets = facets.filter((facet) =>
    SINGLE_ITEM_DIMENSIONS.includes(facet.dimensionName),
  );

  const nonSingleItemFacets = facets.filter(
    (facet) => !SINGLE_ITEM_DIMENSIONS.includes(facet.dimensionName),
  );

  const isShowMoreOrFewerVisible =
    !configProps?.isDrawer &&
    isFacetLimitOn.value &&
    nonSingleItemFacets.length > MAX_NUMBER_TO_SHOW_BEFORE_PROMPTING;

  const maxFacetsToDisplay =
    isShowMoreOrFewerVisible && !isFacetListExpanded
      ? INITIAL_NUMBER_TO_SHOW
      : nonSingleItemFacets.length;

  const dispatchFacetAnalytics = createDispatchFacetAnalytics({
    pageType: route.pageType,
    results: productListingData.results,
    getSelectedFacets: () =>
      getFacetsWithSelectedValues(productListingData.facets),
  });

  return (
    <>
      <ul
        className={cx("facets", {
          "facets--desktopOnly": configProps?.desktopOnly,
          "facets--drawer": configProps?.isDrawer,
          "facets--withPadding": configProps?.filterDrawerPadding,
        })}
        data-component="Facets"
        data-testid="facets"
        onClick={() => {
          sendMonetateEvent("plp.facets.interaction");
        }}
      >
        {singleItemFacets.map(
          (facet) =>
            facet.details[0] && (
              <li
                key={facet.dimensionName}
                className={cx("facet", "facet--singleItem")}
                data-testid="facet-single-item"
              >
                <FacetItem
                  dimensionName={facet.dimensionName}
                  facetName={facet.name}
                  facetDetails={facet.details[0]}
                  dispatchFacetAnalytics={dispatchFacetAnalytics}
                />
              </li>
            ),
        )}
        {nonSingleItemFacets
          .sort((a, b) => {
            if (a.dimensionName === DEPARTMENT_DIMENSION) return -1;
            if (b.dimensionName === DEPARTMENT_DIMENSION) return 1;
            return 0;
          })
          .map((facet, ind) => {
            const isTargetFacet =
              targetFacetName && facet.name === targetFacetName;

            return (
              <li
                key={facet.dimensionName}
                className={styles.facet}
                data-testid="facet-group"
                hidden={ind >= maxFacetsToDisplay && !isTargetFacet}
              >
                <FacetGroup
                  dispatchFacetAnalytics={dispatchFacetAnalytics}
                  facet={{
                    ...facet,
                    name:
                      facet.dimensionName === DEPARTMENT_DIMENSION
                        ? "Select a Department"
                        : facet.name,
                  }}
                  open={
                    targetFacetName
                      ? !!isTargetFacet
                      : !isAllFacetsClosedFlagOn?.value &&
                        ind < NUMBER_OF_OPEN_FACET_ACCORDIONS
                  }
                  position={ind + 1}
                />
              </li>
            );
          })}
      </ul>
      {isShowMoreOrFewerVisible && !targetFacetName && (
        <TertiaryButton
          data-testid="toggle-more-facets"
          onClick={() => setIsFacetListExpanded(!isFacetListExpanded)}
          className={styles["toggle-more-facets"]}
          small
          IconComponent={
            isFacetListExpanded ? Minus24PxOutlinedIcon : Plus24PxOutlinedIcon
          }
        >
          Show {isFacetListExpanded ? "fewer" : "more"} filters
        </TertiaryButton>
      )}
    </>
  );
}
